import React, { useState, useEffect } from 'react'
import { VERSION_HISTORY } from '../constants'
import { useTranslate } from '../hooks/useTranslate'

interface VersionHistoryPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslate();
    // Derive display data from the centralized VERSION_HISTORY constant
    const versions = VERSION_HISTORY.map((entry, index) => ({
        version: entry.version,
        releaseDate: entry.date,
        isLatest: index === 0,
        highlights: entry.features
    }));

    // Animation states — default to the latest (first) version
    const latestVersion = versions.length > 0 ? versions[0].version : '';
    const [expandedVersion, setExpandedVersion] = useState<string>(latestVersion);
    const [isClosing, setIsClosing] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Handle smooth mount/unmount animations + reset to latest on open
    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            setMounted(true);
            setExpandedVersion(latestVersion);
        }
    }, [isOpen, latestVersion]);

    const handleClose = () => {
        setIsClosing(true);
        // Wait for animation to complete before actually closing
        setTimeout(() => {
            setMounted(false);
            onClose();
        }, 1300); // 1200ms animation + 100ms buffer
    };

    const toggleVersion = (version: string) => {
        setExpandedVersion(prev => prev === version ? '' : version);
    };

    if (!isOpen && !mounted) return null;

    return (
        <div
            className={`fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto p-2 sm:p-4 transition-all duration-[1200ms] ease-in-out ${isClosing
                    ? 'bg-black/0'
                    : 'bg-black/40'
                }`}
            role="presentation"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="version-history-title"
                className={`bg-white rounded-xl shadow-2xl w-full max-w-6xl my-auto max-h-[90vh] overflow-hidden flex flex-col transition-all duration-[1200ms] ease-in-out ${isClosing
                        ? 'scale-[0.08] opacity-0 transform translate-x-[75vw] translate-y-[-55vh] rotate-[18deg]'
                        : 'scale-100 opacity-100 transform translate-x-0 translate-y-0 rotate-0'
                    }`}
                style={{
                    transformOrigin: isClosing ? 'top right' : 'center'
                }}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                    <h2 id="version-history-title" className="text-lg sm:text-2xl font-semibold text-gray-900">
                        {t('Version History')}
                    </h2>
                    <button
                        onClick={handleClose}
                        aria-label="Close version history"
                        className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                    <div className="space-y-4">
                        {versions.map((versionInfo) => {
                            const isExpanded = expandedVersion === versionInfo.version;

                            return (
                                <div
                                    key={versionInfo.version}
                                    className={`rounded-xl border shadow-sm bg-white transition-all duration-300 ${isExpanded ? 'border-[#C8102E] ring-1 ring-[#C8102E]/20' : 'border-gray-200'
                                        }`}
                                >
                                    {/* Version Card Header */}
                                    <button
                                        onClick={() => toggleVersion(versionInfo.version)}
                                        className="w-full px-3 sm:px-6 py-3 sm:py-4 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 hover:bg-gray-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#C8102E] focus:ring-inset rounded-xl"
                                        aria-expanded={isExpanded}
                                        aria-controls={`version-content-${versionInfo.version}`}
                                    >
                                        {/* Left: Version */}
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <span className="text-base sm:text-lg font-semibold text-gray-900">
                                                {t('Version')} {versionInfo.version}
                                            </span>
                                            {versionInfo.isLatest && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#C8102E] text-white">
                                                    {t('Latest')}
                                                </span>
                                            )}
                                        </div>

                                        {/* Right: Date and Toggle */}
                                        <div className="flex items-center gap-2 sm:gap-4">
                                            <span className="text-xs sm:text-sm text-gray-600">
                                                {t('Release Date:')} {versionInfo.releaseDate}
                                            </span>

                                            {/* Expand/Collapse Icon */}
                                            <div className={`w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center transition-all duration-300 ${isExpanded ? 'bg-[#C8102E] border-[#C8102E] text-white' : 'bg-white text-gray-600 hover:border-gray-400'
                                                }`}>
                                                <svg
                                                    className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-45' : ''}`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                            </div>
                                        </div>
                                    </button>

                                    {/* Expanded Content */}
                                    {isExpanded && (
                                        <div
                                            id={`version-content-${versionInfo.version}`}
                                            className="px-3 sm:px-6 pb-4 sm:pb-6 border-t border-gray-100"
                                        >
                                            <div className="pt-4">
                                                {/* Highlights */}
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900 mb-3">{t('Key Features & Improvements')}</h4>
                                                    <ul className="space-y-2">
                                                        {versionInfo.highlights.map((highlight, index) => (
                                                            <li key={index} className="flex items-start gap-2">
                                                                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                                                                <span className="text-sm text-gray-700 leading-relaxed">{t(highlight)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-end">
                    <button
                        onClick={handleClose}
                        className="px-6 py-2 bg-[#C8102E] text-white rounded-lg text-sm font-medium hover:bg-[#B00E28] focus:outline-none focus:ring-2 focus:ring-[#C8102E] focus:ring-offset-2 transition-colors"
                    >
                        {t('Close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VersionHistoryPanel;